import * as React from 'react';

import { cn } from '../../../lib/utils';

import { useStatePlaceholderContext } from './state-placeholder-context';
import {
  DEFAULT_TITLE_ELEMENT,
  ICON_CLASSES,
  TILE_CLASSES,
  TITLE_CLASSES,
} from './state-placeholder-variants';

export interface StatePlaceholderSlotProps {
  children?: React.ReactNode;
  className?: string;
}

export interface StatePlaceholderIconProps extends StatePlaceholderSlotProps {
  /** Styles the bordered tile that `panel` and `page` draw around the glyph. */
  tileClassName?: string;
}

/**
 * The glyph, and — at `panel`/`page` — the tile around it.
 *
 * Children are wrapped rather than passed through: a bare node would inherit no sizing and no
 * `aria-hidden`, and would then be read aloud as part of the live region's message.
 */
const StatePlaceholderIcon = ({
  children,
  className,
  tileClassName,
}: StatePlaceholderIconProps) => {
  const { density, testId } = useStatePlaceholderContext();

  // `[&_svg]:size-full` sizes the icon from the wrapper instead of the caller's element, so a
  // lucide glyph (24px by default) and a custom node both land at the density's size.
  const glyph = (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center [&_svg]:size-full',
        ICON_CLASSES[density],
        className,
      )}
    >
      {children}
    </span>
  );

  return (
    // `shrink-0` is belt-and-braces, not a bug fix. This span is the root's flex item, and a
    // flex item's automatic minimum size (`min-height: auto` -> the 80px tile's min-content)
    // already stops it compressing in a squeezed column — measured at a 90px container, with
    // and without this class, and the tile held 80px both ways. It only collapses once
    // `min-height: 0` is ALSO set, which nothing does today: `RouteNotFound`'s `min-h-0` is on
    // the ROOT, governing the root as its parent's flex item, not these children. Stated
    // explicitly so a future `min-h-0` here cannot silently defeat the protection.
    <span data-testid={testId('icon')} className="shrink-0">
      {density === 'inline' ? (
        glyph
      ) : (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center border border-border',
            TILE_CLASSES[density],
            tileClassName,
          )}
        >
          {glyph}
        </span>
      )}
    </span>
  );
};

/** Groups the title and description so they sit closer to each other than to the tile. */
const StatePlaceholderContent = ({
  children,
  className,
}: StatePlaceholderSlotProps) => (
  <div className={cn('flex flex-col gap-1', className)}>{children}</div>
);

export interface StatePlaceholderTitleProps extends StatePlaceholderSlotProps {
  /**
   * Overrides the element the density picks. Use `as="h2"` inside a host page that already
   * owns the `h1` (MFE parcels), or `as="p"` to drop out of the document outline entirely.
   */
  as?: 'p' | 'h1' | 'h2' | 'h3';
}

const StatePlaceholderTitle = ({
  children,
  className,
  as,
}: StatePlaceholderTitleProps) => {
  const { density, testId, registerTitle } = useStatePlaceholderContext();

  // Tells the Root a heading exists. Child effects run before parent effects, so the Root's
  // dev warning always sees this registration.
  React.useEffect(() => {
    registerTitle();
  }, [registerTitle]);

  const Element = as ?? DEFAULT_TITLE_ELEMENT[density];

  return (
    <Element
      data-testid={testId('title')}
      className={cn('text-foreground', TITLE_CLASSES[density], className)}
    >
      {children}
    </Element>
  );
};

/**
 * Takes children, not a string: the copy has to be able to name who can lift the state — an
 * administrator, a different clinic — and that is often a link.
 */
const StatePlaceholderDescription = ({
  children,
  className,
}: StatePlaceholderSlotProps) => {
  const { density, testId } = useStatePlaceholderContext();

  return (
    <p
      data-testid={testId('description')}
      className={cn(
        'text-sm leading-5 text-gray-600',
        density !== 'inline' && 'max-w-md',
        '[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
        className,
      )}
    >
      {children}
    </p>
  );
};

const StatePlaceholderAction = ({
  children,
  className,
}: StatePlaceholderSlotProps) => {
  const { testId } = useStatePlaceholderContext();

  return (
    // `--ring` is gray-200/gray-800, which measures 1.24:1 light and 1.21:1 dark against the
    // surrounding card — well under the 3:1 WCAG 2.2 SC 2.4.11 floor, so a keyboard user
    // cannot see where focus is. Scoped to this slot deliberately: fixing `--ring` itself
    // would restyle focus on every button in the product and needs its own change.
    // TODO(--ring): raise the token to 3:1 against card and delete this override. Until then
    // every new placeholder surface inherits the workaround rather than the fix.
    <div
      data-testid={testId('action')}
      className={cn('[&_:focus-visible]:!ring-primary', className)}
    >
      {children}
    </div>
  );
};

/** Trailing line below the description, e.g. a "coming soon" note. */
const StatePlaceholderFooter = ({
  children,
  className,
}: StatePlaceholderSlotProps) => {
  const { testId } = useStatePlaceholderContext();

  return (
    <p
      data-testid={testId('footer')}
      className={cn('text-sm leading-5 text-gray-600', className)}
    >
      {children}
    </p>
  );
};

StatePlaceholderIcon.displayName = 'StatePlaceholder.Icon';
StatePlaceholderContent.displayName = 'StatePlaceholder.Content';
StatePlaceholderTitle.displayName = 'StatePlaceholder.Title';
StatePlaceholderDescription.displayName = 'StatePlaceholder.Description';
StatePlaceholderAction.displayName = 'StatePlaceholder.Action';
StatePlaceholderFooter.displayName = 'StatePlaceholder.Footer';

export {
  StatePlaceholderAction,
  StatePlaceholderContent,
  StatePlaceholderDescription,
  StatePlaceholderFooter,
  StatePlaceholderIcon,
  StatePlaceholderTitle,
};
